/**
 * Lock the SDMS Univer sheet down to RESULT ENTRY only — deny the STRUCTURAL
 * spreadsheet actions (insert / delete / hide / resize of rows & columns) that
 * Univer's default cell / row / column right-click menus expose but OG's SDMS
 * sheet never allows.
 *
 * OG loads its ASPxSpreadsheet with the Home ribbon stripped and the sheet
 * effectively locked to typing results into template cells — the analyst cannot
 * delete a column, hide one, insert rows, resize, etc. Univer ships all of those
 * on the right-click menu, so we DENY the matching WORKSHEET permission points.
 * Univer's built-in SheetPermissionCheckController then BOTH greys the menu items
 * out AND blocks the underlying commands (so a keyboard shortcut can't sneak past
 * either). This is the proper before-execution guard — not a reactive undo — and
 * it supersedes the older "re-hide on unhide command" lock (kept as belt-and-braces).
 *
 * Kept ALLOWED: cell value entry (typing results), copy, clear-contents — the
 * analyst still edits results and fixes typos. Cell VALUE editing is governed by
 * the workbook editable permission (toggled per View/Enter mode elsewhere), which
 * this does NOT touch — so entry still works in Enter mode and stays read-only in View.
 *
 * Pass the `univer` INSTANCE (the injector hangs off it, exactly like registerLimsRound),
 * plus the unit id and every sheet id (the SDMS workbook has one sheet per run/level).
 * Best-effort: a failure must not take the sheet down, but it is logged loudly —
 * silently losing the lock would let an analyst corrupt the template layout.
 */
import { IPermissionService } from '@univerjs/core';
import {
  WorksheetInsertRowPermission,
  WorksheetDeleteRowPermission,
  WorksheetInsertColumnPermission,
  WorksheetDeleteColumnPermission,
} from '@univerjs/sheets';

type PointCtor = new (unitId: string, subUnitId: string) => { id: string; value: boolean };

/** The DATA-CORRUPTING structural ops denied on every SDMS sheet: insert / delete of
 *  rows & columns (these shift the grid and mis-point the QC formulas).
 *
 *  NOTE — `WorksheetSetColumnStyle` / `WorksheetSetRowStyle` (hide / show / resize) are
 *  DELIBERATELY NOT denied. Denying them made clicking the hidden-column "reveal" arrow
 *  (a set-cols-visible = column-style op) trip Univer's permission layer, which popped a
 *  "protected sheet" alert (user 2026-09-11). Instead: the Hide / Fit-for-data / resize
 *  menu items are removed via `SDMS_CONTEXT_MENU_HIDE`, and the reveal arrow is handled by
 *  the reactive re-hide lock in `SdmsUniverSheet.tsx` (it silently re-hides a template-hidden
 *  column the instant a `…cols-visible` command runs) — so the click is effectively ignored
 *  with NO dialog, while cosmetic column resize (harmless, doesn't touch data) is allowed. */
const BLOCKED_POINTS: PointCtor[] = [
  WorksheetInsertRowPermission,
  WorksheetDeleteRowPermission,
  WorksheetInsertColumnPermission,
  WorksheetDeleteColumnPermission,
];

interface PermissionServiceLite {
  updatePermissionPoint?: (id: string, value: boolean) => void;
  getPermissionPoint?: (id: string) => unknown;
  addPermissionPoint?: (point: unknown) => boolean;
}

export function restrictSdmsSheetActions(univer: unknown, unitId: string, subUnitIds: string[]): void {
  try {
    const injector = (univer as { __getInjector?: () => { get: (id: unknown) => unknown } })?.__getInjector?.();
    if (!injector) {
      // eslint-disable-next-line no-console
      console.error('[SDMS] Univer injector unavailable — structural sheet actions NOT restricted; '
        + 'the right-click menu can still delete/insert/hide rows & columns.');
      return;
    }
    const perm = injector.get(IPermissionService) as PermissionServiceLite | undefined;
    if (!perm?.updatePermissionPoint) {
      // eslint-disable-next-line no-console
      console.error('[SDMS] IPermissionService unavailable — structural sheet actions NOT restricted.');
      return;
    }
    for (const subUnitId of subUnitIds) {
      for (const Point of BLOCKED_POINTS) {
        const point = new Point(unitId, subUnitId);
        // The init controller adds the default (value=true) points on sheet creation; if a
        // point somehow isn't there yet, add it first so the deny below has something to set.
        if (perm.getPermissionPoint && !perm.getPermissionPoint(point.id) && perm.addPermissionPoint) {
          try { perm.addPermissionPoint(point); } catch { /* already present */ }
        }
        perm.updatePermissionPoint(point.id, false);
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[SDMS] structural sheet-action restriction failed', e);
  }
}
